'use client';

import HeadlinePlugin from '@/components/customFields/HeadlinePlugin';
import { HeadlineTag } from '@/components/customFields/types';
import MainAsideLayout from '@/components/mainAsideLayout/MainAsideLayout';
import NestedBlocks from '@/components/nestedBlocks/NestedBlocks';
import { RichText, RichText as RichTextComponent } from '@/components/richText/RichText';
import {
  SwdAccordionHeader,
  SwdAccordionItem,
  SwdAccordionPanel,
  SwdAccordionRoot,
  SwdAccordionTrigger,
} from '@/components/swdAccordion/SwdAccordion';
import type { ComponentAccordionStoryblok, PartialAccordionItemStoryblok } from '@/types/component-types-sb';
import { ColumnWidth } from '@/types/layoutTypes';
import { storyblokEditable } from '@storyblok/react/rsc';
import classNames from 'classnames';
import React, { useState } from 'react';

import classes from './Accordion.module.scss';

const Accordion: React.FC<{ blok: ComponentAccordionStoryblok }> = ({ blok }) => {
  const [accordionItemObject, setAccordionItemObject] = useState<{ [uid: string]: boolean }>(
    Object.fromEntries(blok.children.map((item) => [item._uid, item.open ?? false]))
  );

  const main = (
    <div {...storyblokEditable(blok)}>
      {blok.children?.map((accordion: PartialAccordionItemStoryblok) => (
        <SwdAccordionRoot
          key={accordion._uid}
          className={classNames(classes.partialAccordion)}
          value={accordionItemObject[accordion._uid] ? [accordion._uid] : []}
          onValueChange={(values) => {
            const isOpen = values.includes(accordion._uid);
            if (blok.simultaneouslyOpening) {
              setAccordionItemObject((prev) => ({
                ...prev,
                [accordion._uid]: isOpen,
              }));
            } else {
              setAccordionItemObject(
                Object.fromEntries(blok.children.map((c) => [c._uid, c._uid === accordion._uid && isOpen]))
              );
            }
          }}
        >
          <SwdAccordionItem value={accordion._uid}>
            <SwdAccordionHeader
              render={(props) => {
                const Tag = (blok.elements_heading_hierarchy || 'h3') as React.ElementType;
                return (
                  <Tag {...props} className={props.className}>
                    {props.children}
                  </Tag>
                );
              }}
            >
              <SwdAccordionTrigger>{accordion.title}</SwdAccordionTrigger>
            </SwdAccordionHeader>
            <SwdAccordionPanel className={classNames(classes.partialAccordionDetails)}>
              <RichTextComponent text={accordion.text} />
              <NestedBlocks blocks={accordion.content} />
            </SwdAccordionPanel>
          </SwdAccordionItem>
        </SwdAccordionRoot>
      ))}
    </div>
  );
  const aside = <RichText text={blok.richtext} />;

  return (
    <>
      <MainAsideLayout
        main={main}
        aside={aside}
        above={<HeadlinePlugin data={blok.headline} defaultTag={HeadlineTag.H2} defaultStyle={HeadlineTag.H3} />}
        flipLayout={blok.flip_layout}
        mainColumnWidth={{ desktopUp: ColumnWidth.TWO_THIRD }}
        blok={blok}
        anchor={blok.anchor}
      />
    </>
  );
};

export default Accordion;
